import React, { useMemo } from "react";
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import { usePartnerUnits, useUtilitiesStatus } from "services/hooks/utilities";

const formatKwh = (value) => (value == null ? null : `${value} kWh`);
const formatM3 = (value) => (value == null ? null : `${value} m³`);

/**
 * Controlled utilities (prepaid meters) section for unit create / edit.
 * value shape: { enabled, provider_account_number, electricity_enabled, water_enabled, notes }
 */
const UnitUtilitiesSection = ({ value, onChange, error }) => {
	const { configured, loading: statusLoading } = useUtilitiesStatus();
	const { units, loading: unitsLoading, error: unitsError } = usePartnerUnits({
		enabled: Boolean(value?.enabled) && configured,
		unmappedOnly: true,
	});

	const options = useMemo(() => {
		const list = Array.isArray(units) ? [...units] : [];
		const current = value?.provider_account_number;
		if (current && !list.some((u) => String(u.account_number) === String(current))) {
			list.unshift({
				account_number: current,
				label: `Current account (${current})`,
				complex_name: "",
				has_electricity: true,
				has_water: true,
				mapped: true,
			});
		}
		return list;
	}, [units, value?.provider_account_number]);

	const selectedUnit = useMemo(
		() => options.find((u) => String(u.account_number) === String(value?.provider_account_number)),
		[options, value?.provider_account_number]
	);

	const patch = (partial) => onChange({ ...value, ...partial });

	const handleSelectAccount = (accountNumber) => {
		const unit = options.find((u) => String(u.account_number) === String(accountNumber));
		patch({
			provider_account_number: accountNumber,
			...(unit
				? {
						electricity_enabled: unit.has_electricity !== false,
						water_enabled: unit.has_water !== false,
				  }
				: {}),
		});
	};

	const toggleDisabled = statusLoading || configured === false;

	return (
		<Box sx={{ border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "4px", padding: "1rem" }}>
			<Typography variant="h5" sx={{ mb: 1 }}>
				Utilities (prepaid meters)
			</Typography>
			{statusLoading ? (
				<CircularProgress size={22} />
			) : (
				<>
					{!configured && (
						<Alert severity="info" sx={{ mb: 1 }}>
							Utilities provider is not configured. Enablement is unavailable.
						</Alert>
					)}
					<FormControlLabel
						control={
							<Switch
								checked={Boolean(value?.enabled)}
								onChange={(e) =>
									patch({
										enabled: e.target.checked,
										...(e.target.checked
											? {}
											: { provider_account_number: value?.provider_account_number || "" }),
									})
								}
								disabled={toggleDisabled}
							/>
						}
						label="Enable utilities"
					/>

					{value?.enabled && (
						<Box display="flex" flexDirection="column" gap={2} mt={1}>
							{unitsLoading && (
								<Box display="flex" alignItems="center" gap={1}>
									<CircularProgress size={18} />
									<Typography variant="body2" color="text.secondary">
										Loading partner accounts…
									</Typography>
								</Box>
							)}
							{unitsError && <Alert severity="error">{unitsError}</Alert>}
							<FormControl fullWidth size="small" error={Boolean(error)}>
								<InputLabel>Provider account</InputLabel>
								<Select
									label="Provider account"
									value={value.provider_account_number || ""}
									onChange={(e) => handleSelectAccount(e.target.value)}
								>
									{options.length === 0 && !unitsLoading && (
										<MenuItem value="" disabled>
											No unmapped accounts available
										</MenuItem>
									)}
									{options.map((unit) => {
										const elecBalance = formatKwh(unit.electricity_balance_kwh);
										const waterBalance = formatM3(unit.water_balance_m3);
										return (
											<MenuItem key={unit.account_number} value={unit.account_number}>
												<Box>
													<Typography variant="body2">
														{unit.account_number}
														{unit.label ? ` — ${unit.label}` : ""}
														{unit.complex_name ? ` (${unit.complex_name})` : ""}
													</Typography>
													<Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
														{unit.has_electricity !== false && (
															<Typography variant="caption" color="text.secondary">
																⚡ {elecBalance ?? "balance n/a"}
															</Typography>
														)}
														{unit.has_water !== false && (
															<Typography variant="caption" color="text.secondary">
																💧 {waterBalance ?? "balance n/a"}
															</Typography>
														)}
													</Stack>
												</Box>
											</MenuItem>
										);
									})}
								</Select>
							</FormControl>
							{error && (
								<Typography variant="caption" color="error">
									{error}
								</Typography>
							)}

							{selectedUnit && (
								<Stack direction="row" spacing={1} flexWrap="wrap">
									{selectedUnit.has_electricity !== false && (
										<Chip
											size="small"
											icon={<BoltIcon />}
											label={`Electricity: ${formatKwh(selectedUnit.electricity_balance_kwh) ?? "balance n/a"}`}
											variant="outlined"
										/>
									)}
									{selectedUnit.has_water !== false && (
										<Chip
											size="small"
											icon={<WaterDropIcon />}
											label={`Water: ${formatM3(selectedUnit.water_balance_m3) ?? "balance n/a"}`}
											variant="outlined"
										/>
									)}
									{selectedUnit.has_electricity === false && selectedUnit.has_water === false && (
										<Typography variant="caption" color="text.secondary">
											This account has no metered utilities reported.
										</Typography>
									)}
								</Stack>
							)}

							<FormControlLabel
								control={
									<Switch
										checked={value.electricity_enabled !== false}
										onChange={(e) => patch({ electricity_enabled: e.target.checked })}
										disabled={selectedUnit ? selectedUnit.has_electricity === false : false}
									/>
								}
								label="Electricity"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={value.water_enabled !== false}
										onChange={(e) => patch({ water_enabled: e.target.checked })}
										disabled={selectedUnit ? selectedUnit.has_water === false : false}
									/>
								}
								label="Water"
							/>
							<TextField
								size="small"
								label="Notes"
								value={value.notes || ""}
								onChange={(e) => patch({ notes: e.target.value })}
								fullWidth
								multiline
								rows={2}
							/>
						</Box>
					)}
				</>
			)}
		</Box>
	);
};

export default UnitUtilitiesSection;
